import { HotelReserveCardRacksPropertiesTypes } from "@website/accommodation-ui/hotel-reserve-rooms-layout/hotel-reserve-card/hotel-reserve-card-racks/hotel-reserve-card-racks.types";
import { useJwtToken } from "@website/hooks/auth-manager";
import { MiniChips } from "@website/ui/mini-chips";
import { dateFormat } from "@website/utils/date-format";
import { numberConvert } from "@website/utils/number-convert";
import { numberSeparator } from "@website/utils/number-separator";
import { openLoginModal } from "@website/widget/login";
import omit from "lodash-es/omit";
import toNumber from "lodash-es/toNumber";
import { useRouter } from "next/router";
import { ChangeEvent, useCallback, useEffect, useState } from "react";

export const useHotelReserveRacks = ({
  roomRacks,
  open
}: HotelReserveCardRacksPropertiesTypes) => {
  const router = useRouter();
  const [roomCount, setRoomCount] = useState(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [packageSelect, setPackageSelect] = useState(
    roomRacks?.[toNumber(router?.query?.["packages"]) - 1]
  );

  const { phoneNumber } = useJwtToken();

  useEffect(() => {
    setPackageSelect(roomRacks?.[toNumber(router?.query?.["packages"]) - 1]);
  }, [roomRacks]);

  useEffect(() => {
    if (packageSelect)
      setRoomCount(
        Number.isNaN(Number(router.query["no_rooms"]))
          ? 1
          : Number(router.query["no_rooms"]) <= packageSelect?.availableRoom ??
              1
            ? Number(router.query["no_rooms"])
            : packageSelect?.availableRoom
      );
  }, [packageSelect, router]);

  const items = roomRacks?.map((rack, index) => ({
    label: (
      <div className="flex w-full flex-row flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-subtitle-2 flex flex-row items-center gap-2">
            <div>
              {dateFormat(rack.dateFrom, {
                locale: router.locale,
                template: "dd MMMM"
              })}
            </div>
            <span>الی</span>
            <div>
              {dateFormat(rack.dateTo, {
                locale: router.locale,
                template: "dd MMMM"
              })}
            </div>
          </div>
        </div>
        <div className="text-on-surface-medium-emphasis">
          {Boolean(rack?.price - rack?.priceOff) && (
            <div className="flex flex-row items-center gap-2">
              <span className="text-body-2 text-on-surface-medium-emphasis line-through">
                {numberConvert(numberSeparator(rack?.price), {
                  locale: router.locale
                })}
              </span>
              <MiniChips
                color="primary"
                text={`${numberConvert(
                  (
                    ((rack?.price - rack?.priceOff) / rack?.price) *
                    100
                  ).toFixed(0),
                  {
                    locale: router.locale
                  }
                )}%`}
                variant="contained"
              />
            </div>
          )}
          {numberConvert(
            numberSeparator(
              rack?.price - rack?.priceOff ? rack?.priceOff : rack?.price
            ),
            {
              locale: router.locale
            }
          )}
          <span className="text-body-2 ps-1">تومان</span>
        </div>
      </div>
    ),
    value: index + 1
  }));

  useEffect(() => {
    if (!open)
      router?.push(
        {
          pathname: router?.pathname,
          hash: window?.location?.hash,
          query: {
            ...omit(router?.query, "packages")
          }
        },
        undefined,
        { shallow: true, scroll: false }
      );
  }, [open]);

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      router?.push(
        {
          pathname: router?.pathname,
          query: {
            ...router.query,
            packages: event?.target?.value
          }
        },
        undefined,
        { shallow: true, scroll: false }
      ),
    [router?.query]
  );

  const onHandleClick = useCallback(() => {
    if (!phoneNumber)
      return openLoginModal(router).then(() => {
        if (packageSelect) {
          setLoading(true);
          packageSelect?.onClick?.()?.finally?.(() => setLoading(false));
        }
      });
    if (packageSelect) {
      setLoading(true);
      packageSelect?.onClick?.()?.finally?.(() => setLoading(false));
    }
  }, [packageSelect, phoneNumber, router]);

  const handleRoomCount = useCallback(
    (count: number) => {
      setRoomCount(count);
      return router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            no_rooms: count
          }
        },
        undefined,
        { scroll: false, shallow: true }
      );
    },
    [router]
  );

  return {
    items,
    onChange,
    packageSelect,
    roomCount,
    setRoomCount: handleRoomCount,
    onHandleClick,
    loading,
    selectedPackageIndex: router?.query?.["packages"]
  };
};
